import { proxy } from "valtio";
import { toast } from "sonner";

/**
 * UI store for theme, sidebar, and other UI state
 */
interface UIStore {
  /** Current theme */
  theme: "light" | "dark" | "system";
  /** Whether sidebar is collapsed (desktop) */
  sidebarCollapsed: boolean;
  /** Whether mobile sidebar is open */
  mobileSidebarOpen: boolean;
  /** Currently active modal */
  activeModal: string | null;
}

export const uiStore = proxy<UIStore>({
  theme: "system",
  sidebarCollapsed: false,
  mobileSidebarOpen: false,
  activeModal: null,
});

/** Set theme and persist to localStorage */
export function setTheme(theme: "light" | "dark" | "system") {
  uiStore.theme = theme;
  localStorage.setItem("vizi-theme", theme);

  // Apply theme to document
  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  document.documentElement.classList.toggle("dark", isDark);
}

/** Toggle sidebar collapsed state (desktop) */
export function toggleSidebar() {
  uiStore.sidebarCollapsed = !uiStore.sidebarCollapsed;
}

/** Open mobile sidebar */
export function openMobileSidebar() {
  uiStore.mobileSidebarOpen = true;
}

/** Close mobile sidebar */
export function closeMobileSidebar() {
  uiStore.mobileSidebarOpen = false;
}

/** Toggle mobile sidebar */
export function toggleMobileSidebar() {
  uiStore.mobileSidebarOpen = !uiStore.mobileSidebarOpen;
}

/** Open a modal */
export function openModal(modalId: string) {
  uiStore.activeModal = modalId;
}

/** Close current modal */
export function closeModal() {
  uiStore.activeModal = null;
}

interface ToastOptions {
  type: "success" | "error" | "info" | "warning";
  message: string;
  duration?: number;
}

/** Add a toast notification using Sonner */
export function addToast({ type, message, duration = 5000 }: ToastOptions) {
  const options = { duration };

  switch (type) {
    case "success":
      return toast.success(message, options);
    case "error":
      return toast.error(message, options);
    case "warning":
      return toast.warning(message, options);
    case "info":
    default:
      return toast.info(message, options);
  }
}

/** Show a success toast */
export function showSuccess(message: string) {
  return toast.success(message);
}

/** Show an error toast */
export function showError(message: string) {
  return toast.error(message);
}

/** Show an info toast */
export function showInfo(message: string) {
  return toast.info(message);
}

/** Show a warning toast */
export function showWarning(message: string) {
  return toast.warning(message);
}

/** Initialize theme from localStorage and set up system preference listener */
export function initializeTheme() {
  const saved = localStorage.getItem("vizi-theme") as
    | "light"
    | "dark"
    | "system"
    | null;
  if (saved) {
    setTheme(saved);
  } else {
    setTheme("system");
  }

  // Listen for system preference changes
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", (e) => {
      if (uiStore.theme === "system") {
        document.documentElement.classList.toggle("dark", e.matches);
      }
    });
}
